import React, { useEffect, useRef, useState } from 'react'
import { Box } from '@mui/material'
import { Button, CircularProgress, Container, makeStyles, Typography } from '@material-ui/core'
import { PollStatus } from 'constants/index'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import pollingSlice, {
  getActivePollWithinSessionOrSessionTalks,
  getActivePollWithinTalkEvent,
  getActiveQuestionWithinPoll,
  getAnswersOfSpecificPoll,
  getPollById
} from 'store/polling'
import PollQuestion from 'components/PollQuestion'
import { Close, Fullscreen } from '@material-ui/icons'
import { colors } from 'utils/colors'
import PresentationMode from 'views/moderators/polling/PresentationMode'
import { storageRef } from 'index'

const PollPresentation = () => {
  const history = useHistory()
  const fullScreenRef = useRef(null)
  const classes = useStyles()
  const dispatch = useDispatch()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const pollId = queryParams.get('pollId')
  const eventId = queryParams.get('eventId')
  const sessionId = queryParams.get('sessionId')
  const [presentationMode, setPresentationMode] = useState(false)
  const [splashImage, setSplashImage] = useState(null)
  const [loadingImage, setLoadingImage] = useState(false)
  const { inProgress, poll, question } = useSelector((state) => state.polling)
  const { conference } = useSelector((state) => state.conference)

  const downloadImage = async (imageName) => {
    try {
      setLoadingImage(true)
      const imageUrl = await storageRef
        .child(`${conference?.id}/conference/${imageName}`)
        .getDownloadURL()
      setSplashImage(imageUrl)
    } catch (err) {
      console.error(err)
    } finally {
      setLoadingImage(false)
    }
  }

  useEffect(() => {
    if (conference?.confSplashScreenImg) {
      downloadImage(conference?.confSplashScreenImg)
    }
  }, [conference?.confSplashScreenImg])

  useEffect(async () => {
    dispatch(pollingSlice.actions.setProgress(true))
    if (pollId) {
      const unsub = getPollById(pollId, (data) => {
        dispatch(pollingSlice.actions.setPoll(data))
        dispatch(pollingSlice.actions.setProgress(false))
      })
      return () => unsub && unsub()
    } else if (sessionId && !eventId) {
      const unsubActivePoll = getActivePollWithinSessionOrSessionTalks(sessionId, (data) => {
        dispatch(pollingSlice.actions.setPoll(data))
        dispatch(pollingSlice.actions.setProgress(false))
      })
      return () => unsubActivePoll && unsubActivePoll()
    } else if (eventId && !sessionId) {
      const unsubActivePoll = getActivePollWithinTalkEvent(eventId, (data) => {
        dispatch(pollingSlice.actions.setPoll(data))
        dispatch(pollingSlice.actions.setProgress(false))
      })
      return () => unsubActivePoll && unsubActivePoll()
    }
  }, [dispatch, pollId, sessionId, eventId])

  useEffect(() => {
    if (poll?.id) {
      const unsubQuestion = getActiveQuestionWithinPoll(poll?.id, (data) => {
        dispatch(pollingSlice.actions.setQuestion(data))
      })
      return () => unsubQuestion && unsubQuestion()
    }
  }, [dispatch, poll?.id])

  useEffect(() => {
    if (poll?.id) {
      const unsubscribeAnswers = getAnswersOfSpecificPoll(poll?.id, (data) => {
        dispatch(pollingSlice.actions.setAnswers(data))
      })
      return () => unsubscribeAnswers && unsubscribeAnswers()
    }
  }, [dispatch, poll, poll?.id])

  const handleOpenFullScreen = () => {
    if (!presentationMode) {
      setPresentationMode(true)
      if (fullScreenRef.current.requestFullscreen) {
        fullScreenRef.current.requestFullscreen()
      } else if (fullScreenRef.current.mozRequestFullScreen) {
        fullScreenRef.current.mozRequestFullScreen()
      } else if (fullScreenRef.current.webkitRequestFullscreen) {
        fullScreenRef.current.webkitRequestFullscreen()
      } else if (fullScreenRef.current.msRequestFullscreen) {
        fullScreenRef.current.msRequestFullscreen()
      }
    }
  }

  const handleCloseFullScreen = () => {
    if (presentationMode) {
      if (document.exitFullscreen) {
        document.exitFullscreen()
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen()
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen()
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen()
      }
    }
  }

  const handleFullscreenChange = () => {
    if (!document.fullscreenElement) {
      setPresentationMode(false)
      history.replace('/dashboard/polls')
    }
  }
  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setPresentationMode(false)
      handleCloseFullScreen()
    }
  }

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  const goBack = () => {
    history.replace('/dashboard/polls')
  }

  if (inProgress) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: '10rem' }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box ref={fullScreenRef} sx={{ height: '100%' }}>
      <PresentationMode>
        {!poll || poll?.status === PollStatus.CREATED ? (
          <Box className={classes.container}>
            {loadingImage ? (
              <CircularProgress size={25} />
            ) : (
              <img src={splashImage} alt="conference splash screen" width="340px" height="280" />
            )}
            {!presentationMode && (
              <>
                <Box className={classes.content}>
                  <Typography className={classes.boldGuide}>Greetings!</Typography>
                  <br />
                  <Typography className={classes.guide}>
                    Click the Full Screen button below to expand the page to full size. This is
                    temporary page and once the Moderator initiates the poll, this will transition
                    to showcase the live poll interface.
                  </Typography>
                </Box>
                <Box className={classes.actionBtns}>
                  <Button
                    color="primary"
                    variant="contained"
                    className={classes.start}
                    size="large"
                    startIcon={<Fullscreen fontSize="large" className={classes.startIcon} />}
                    onClick={handleOpenFullScreen}>
                    Full Screen
                  </Button>
                  <Button
                    variant="contained"
                    className={classes.cancel}
                    startIcon={<Close fontSize="large" className={classes.startIcon} />}
                    onClick={goBack}>
                    Cancel
                  </Button>
                </Box>
              </>
            )}
          </Box>
        ) : poll?.status === PollStatus.CLOSED || poll?.status === PollStatus.PAUSED ? (
          <Box className={classes.container}>
            <img src={splashImage} alt="conference splash screen" width="340px" height="280" />
          </Box>
        ) : (
          question && (
            <Container className={classes.container}>
              <Box sx={{ position: 'absolute', top: '15%' }}>
                <PollQuestion question={question} />
              </Box>
            </Container>
          )
        )}
      </PresentationMode>
    </Box>
  )
}

export default PollPresentation

const commonStart = {
  color: '#FFFFFF',
  textTransform: 'none',
  width: '240px',
  fontSize: '26px',
  fontWeight: '600',
  letterSpacing: '1.25px',
  borderRadius: '12px',
  borderColor: colors.indigo,
  display: 'flex',
  justifyContent: 'center',
  gap: '4px',
  alignItems: 'center',
  padding: '12px 20px',
  cursor: 'pointer'
}

const useStyles = makeStyles((theme) => ({
  info: {
    paddingTop: '8rem'
  },
  container: {
    [theme.breakpoints.down('sm')]: {
      padding: '1.5rem 1rem'
    },
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  },
  content: {
    maxWidth: '900px'
  },
  guide: {
    fontSize: '20px',
    letterSpacing: '0.5px'
  },
  boldGuide: {
    fontSize: '20px',
    fontWeight: 600,
    letterSpacing: '0.5px'
  },
  start: {
    ...commonStart,
    backgroundColor: colors.indigo,
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: '#007E99'
    }
  },
  actionBtns: {
    marginTop: '46px',
    display: 'flex',
    alignItems: 'center',
    gap: '20px'
  },
  cancel: {
    ...commonStart,
    width: '200px',
    color: '#757575'
  },
  startIcon: {
    fontSize: '35px !important'
  }
}))
